import type { ApolloCache, Reference } from '@apollo/client';
import gql from 'graphql-tag';
import { partition, uniq } from 'lodash';
import type { Apollo } from 'src/gql/generated';

const ARTIFACT_SKILL_FRAGMENT = gql`
  fragment ArtifactSkillFields on AgentSkill {
    id
    name
    scope
    isComplete
    currentVersion {
      id
      name
      description
    }
  }
`;

export interface SkillArtifactCreatedEventForCache {
  agentMessageId: string;
  artifactCreatedAt: string;
  kindList: Apollo.AgentMessageJoinKind[];
  skillId: string;
  skillName: string;
  threadId: string;
}

type ThreadArtifactCacheRow = NonNullable<
  Apollo.GetAgentThreadQuery['agentThread']
>['artifacts'][number];
type ThreadSkillArtifactCacheRow = Extract<
  ThreadArtifactCacheRow,
  { __typename: 'ThreadSkillArtifact' }
>;

/**
 * One row per skill, as the server returns it: a second save of the same skill merges in.
 *
 * The event carries only the skill's id and name, so the row borrows the rest from the
 * cached skill. Returns false when the skill is not cached, and the caller refetches.
 */
export function applySkillArtifactCreatedEventToCache(
  cache: ApolloCache,
  event: SkillArtifactCreatedEventForCache
): boolean {
  const skillCacheId = cache.identify({ __typename: 'AgentSkill', id: event.skillId });
  const cachedSkill = cache.readFragment<ThreadSkillArtifactCacheRow['skill']>({
    fragment: ARTIFACT_SKILL_FRAGMENT,
    id: skillCacheId,
  });
  if (!cachedSkill) return false;

  const row: ThreadSkillArtifactCacheRow = {
    __typename: 'ThreadSkillArtifact',
    agentMessageId: event.agentMessageId,
    createdAt: event.artifactCreatedAt,
    kindList: event.kindList,
    skill: { ...cachedSkill, name: event.skillName },
  };

  cache.writeFragment({ data: row.skill, fragment: ARTIFACT_SKILL_FRAGMENT, id: skillCacheId });
  cache.modify({
    fields: {
      artifacts(existing = [], { readField }) {
        if (!Array.isArray(existing)) return existing;

        const [matchingRows, remainingRows] = partition(existing, artifact => {
          const skillRef = readField<Reference>(
            'skill' satisfies keyof ThreadSkillArtifactCacheRow,
            artifact
          );
          return readField('id', skillRef) === event.skillId;
        });
        const existingKindList = matchingRows.flatMap(
          existingRow =>
            readField<readonly Apollo.AgentMessageJoinKind[]>(
              'kindList' satisfies keyof ThreadSkillArtifactCacheRow,
              existingRow
            ) ?? []
        );

        return [
          {
            ...row,
            kindList: uniq([...existingKindList, ...event.kindList]),
          },
          ...remainingRows,
        ];
      },
    },
    id: cache.identify({ __typename: 'AgentThread', id: event.threadId }),
  });
  return true;
}
